import { useState, useEffect, useRef } from "react"
import { useSidebar } from "../contexts/sidebar-context"
import { useAuth } from "../contexts/auth-context"
import { TranslatedText } from "./translated-text"
import {
  Building2,
  BarChart3,
  Users,
  FileText,
  Settings,
  Brain,
  AlertTriangle,
  CreditCard,
  PieChart,
  Calculator,
  ChevronRight,
  Home,
  ShoppingCart,
  Receipt,
  Banknote,
  Package,
  UserCheck,
  FolderOpen,
  Globe,
  HelpCircle,
  TrendingUp,
  Shield,
  Target,
  Clock,
  Zap,
  DollarSign,
  FileBarChart,
  MessageSquare,
  BookOpen,
  ShoppingBag,
  Wallet,
  Building,
  UserCog,
} from "lucide-react"
import { cn } from "../lib/utils"
import { Link, useLocation } from "react-router-dom"
import { useKeyboardNavigation } from "./keyboard-navigation-provider"

interface NavigationItem {
  name: string
  icon: any
  href: string
  current?: boolean
  favorite?: boolean
  recent?: boolean
  badge?: string
  adminOnly?: boolean
}

interface NavigationGroup {
  name: string
  icon: any
  items: NavigationItem[]
  defaultExpanded?: boolean
  priority?: "core" | "optional" | "utility"
}

const adaptiveMenuConfig = {
  core: ["Dashboard", "Sales & Invoicing", "Purchases & Expenses", "Banking & Cash", "Tax Management"],
  optional: ["Inventory & Products", "Payroll & HR", "Projects & Time", "Multi-Company", "International"],
  utility: [
    "Reports & Analytics",
    "AI Insights",
    "AI Powered Features",
    "Settings",
    "Help & Support",
    "Documents",
    "Client Portal",
    "Security",
  ],
}

const userPreferences = {
  enabledModules: new Set([
    "Dashboard",
    "Sales & Invoicing",
    "Purchases & Expenses",
    "Banking & Cash",
    "Inventory & Products",
    "Accounting",
    "Fixed Assets",
    "Journal Entries Hub",
    "Tax Management",
    "Reports & Analytics",
    "AI Insights",
    "AI Powered Features",
    "Client Portal",
    "System Settings",
  ]),
  favoriteItems: new Set([
    "Financial Overview",
    "AI Smart Insights",
    "AI Powered Features",
    "Client Portal",
    "Invoices",
    "Expenses",
    "Accounts Payable",
    "Journal Entries Hub",
    "Bank Accounts & Reconciliation",
    "Enhanced Financial Reports",
    "Advanced Analytics Dashboard",
    "AI Financial Coach",
    "Fixed Assets",
  ]),
  recentlyUsed: ["Invoices", "Expenses", "Accounts Payable", "AI Smart Insights", "AI Powered Features", "Client Portal", "Bank Accounts & Reconciliation", "Enhanced Financial Reports", "Advanced Analytics Dashboard", "AI Financial Coach", "Fixed Assets"],
}

// Simplified navigation structure - single level for better UX
// Arranged in logical senior/user workflow order: Overview → Revenue → Expenses → Cash → Operations → Accounting → Assets → HR → Settings
const navigationItems: NavigationItem[] = [
  // 1. Overview & Dashboard
  { 
    name: "Dashboard", 
    icon: Home, 
    href: "/dashboard",
    favorite: true
  },
  
  // 2. Revenue & Sales
  { 
    name: "Sales & Invoicing", 
    icon: ShoppingCart, 
    href: "/dashboard/sales",
    badge: "Core"
  },
  { 
    name: "Point of Sale", 
    icon: CreditCard, 
    href: "/dashboard/pos",
    badge: "New",
    favorite: true
  },
  
  // 3. Expenses & Purchases
  { 
    name: "Expenses & Purchases", 
    icon: Receipt, 
    href: "/dashboard/expenses",
    favorite: true,
    recent: true
  },
  { 
    name: "Accounts Payable", 
    icon: Wallet, 
    href: "/dashboard/enhanced-accounts-payable",
    badge: "Enhanced",
    favorite: true
  },
  { 
    name: "Purchase Orders", 
    icon: ShoppingBag, 
    href: "/dashboard/purchase-orders",
    badge: "Procurement",
    favorite: true
  },
  { 
    name: "Supplier Portal", 
    icon: Building2, 
    href: "/dashboard/vendors",
    badge: "Vendors",
    favorite: true
  },
  
  // 4. Cash & Banking
  { 
    name: "Banking & Cash", 
    icon: Banknote, 
    href: "/dashboard/banking",
    favorite: true
  },
  
  // 5. Operations
  { 
    name: "Inventory & Products", 
    icon: Package, 
    href: "/dashboard/inventory"
  },
  
  // 6. Accounting & Financial
  { 
    name: "Accounting", 
    icon: FileText, 
    href: "/dashboard/accounting",
    favorite: true
  },
  { 
    name: "Journal Entries Hub", 
    icon: BookOpen, 
    href: "/dashboard/journal-hub",
    badge: "New",
    favorite: true
  },
  
  // 7. Assets & Fixed Assets
  { 
    name: "Fixed Assets", 
    icon: Building, 
    href: "/dashboard/fixed-assets",
    badge: "Assets",
    favorite: true
  },
  
  // 8. Human Resources
  { 
    name: "Payroll Management", 
    icon: Users, 
    href: "/dashboard/payroll",
    badge: "New",
    favorite: true
  },
  
  // 9. Company & Settings
  { 
    name: "Company", 
    icon: Building2, 
    href: "/dashboard/companies"
  },
  // 10. User Management (Admin only - will be filtered in component)
  { 
    name: "Users", 
    icon: UserCog, 
    href: "/dashboard/users",
    adminOnly: true
  },
  // Commented out items - not shown in sidebar
  // { 
  //   name: "Supplier Portal", 
  //   icon: Building2, 
  //   href: "/dashboard/supplier-portal",
  //   badge: "New",
  //   favorite: true
  // },
  // { 
  //   name: "Tax Management", 
  //   icon: Calculator, 
  //   href: "/dashboard/tax",
  //   badge: "Enhanced"
  // },
  // { 
  //   name: "Tax Calculation", 
  //   icon: Calculator, 
  //   href: "/dashboard/tax-calculation",
  //   badge: "New"
  // },
  // { 
  //   name: "Approval Hub", 
  //   icon: UserCheck, 
  //   href: "/dashboard/approval-hub",
  //   badge: "New",
  //   favorite: true
  // },
  // { 
  //   name: "Smart Ledger", 
  //   icon: MessageSquare, 
  //   href: "/dashboard/enhanced-journal-management",
  //   badge: "Smart"
  // },
  // { 
  //   name: "Reports & Analytics", 
  //   icon: BarChart3, 
  //   href: "/dashboard/reports",
  //   favorite: true
  // },
  // { 
  //   name: "AI Insights", 
  //   icon: Brain, 
  //   href: "/dashboard/ai-insights",
  //   badge: "AI",
  //   favorite: true
  // },
  // { 
  //   name: "AI Powered Features", 
  //   icon: Zap, 
  //   href: "/dashboard/llama-ai",
  //   badge: "AI+",
  //   favorite: true
  // },
  // { 
  //   name: "Documents", 
  //   icon: FolderOpen, 
  //   href: "/dashboard/documents"
  // },
  // { 
  //   name: "Client Portal", 
  //   icon: Users, 
  //   href: "/dashboard/clients",
  //   badge: "Portal",
  //   favorite: true
  // },
  // { 
  //   name: "Settings", 
  //   icon: Settings, 
  //   href: "/dashboard/settings"
  // },
  // { 
  //   name: "Help & Support", 
  //   icon: HelpCircle, 
  //   href: "/dashboard/help-support"
  // }
]

export function Sidebar() {
  const location = useLocation()
  const { isCollapsed, setIsCollapsed } = useSidebar()
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const { setFocusedElement } = useKeyboardNavigation()
  const { user } = useAuth()
  
  // Filter navigation items based on user role
  const filteredNavigationItems = navigationItems.filter(item => {
    // Show admin-only items only if user is admin
    if (item.adminOnly && user?.role !== 'admin') {
      return false
    }
    return true
  })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement?.closest("[data-sidebar]")) {
        const allItems = document.querySelectorAll("[data-sidebar-item]")

        if (e.key === "ArrowDown") {
          e.preventDefault()
          const nextIndex = Math.min(focusedIndex + 1, allItems.length - 1)
          setFocusedIndex(nextIndex)
          ;(allItems[nextIndex] as HTMLElement)?.focus()
        } else if (e.key === "ArrowUp") {
          e.preventDefault()
          const prevIndex = Math.max(focusedIndex - 1, 0)
          setFocusedIndex(prevIndex)
          ;(allItems[prevIndex] as HTMLElement)?.focus()
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [focusedIndex])



  return (
    <div
      className={cn(
        "fixed left-0 top-0 bg-sidebar border-r border-sidebar-border transition-all duration-300 z-40 hidden lg:flex lg:flex-col",
        // Responsive height: full height on desktop, adapt to content on smaller screens
        "h-screen min-h-screen max-h-screen",
        // Ensure proper flex behavior
        "flex-shrink-0",
        // Responsive behavior for different screen sizes
        "lg:h-screen xl:h-screen 2xl:h-screen",
        // Responsive width
        isCollapsed ? "w-16" : "w-64",
      )}
      data-sidebar
      role="navigation"
      aria-label="Main navigation"
      style={{
        // Ensure sidebar takes full available height
        height: '100vh',
        maxHeight: '100vh',
        // Add responsive behavior
        minHeight: '100vh',
      }}
    >
      <div className="p-4 border-b border-sidebar-border flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5 text-primary-foreground" />
          </div>
          {!isCollapsed && (
            <div>
              <h2 className="text-lg font-bold text-sidebar-foreground">UrutiIQ</h2>
              <p className="text-xs text-muted-foreground">AI Accounting</p>
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="ml-auto p-1 hover:bg-sidebar-accent/50 rounded focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!isCollapsed}
          >
            <ChevronRight className={cn("w-4 h-4 transition-transform", isCollapsed ? "rotate-0" : "rotate-180")} />
          </button>
        </div>
      </div>



      {!isCollapsed && (
        <div className="px-3 py-2 border-b border-sidebar-border flex-shrink-0">
          <h3 className="text-xs font-medium text-muted-foreground mb-2">
            <TranslatedText text="Quick Access" />
          </h3>
          <div className="space-y-1" role="list" aria-label="Quick access items">
            {userPreferences.recentlyUsed.slice(0, 4).map((itemName) => {
              const item = navigationItems.find((i) => i.name === itemName)
              if (!item) return null
              return (
                <Link
                  key={itemName}
                  to={item.href}
                  className="flex items-center gap-2 px-2 py-1.5 text-xs rounded-md hover:bg-teal-50 text-muted-foreground hover:text-teal-700 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1"
                  data-sidebar-item
                  role="listitem"
                  aria-label={`Quick access: ${itemName}`}
                  onFocus={() => setFocusedElement("quick-access")}
                >
                  <item.icon className="w-3 h-3" />
                  <span className="truncate">
                    <TranslatedText text={itemName} />
                  </span>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 sidebar-scrollbar min-h-0 max-h-full" role="navigation" aria-label="Main menu">
        {filteredNavigationItems.map((item) => {
          const isActive = location.pathname === item.href || 
            (item.href?.includes('?') && location.pathname === item.href?.split('?')[0])
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1 border-0 outline-none hover:outline-none hover:ring-0",
                isActive
                  ? "bg-teal-100 text-teal-700 font-medium border-l-2 border-teal-500"
                  : "text-sidebar-foreground hover:bg-teal-50 hover:text-teal-700 hover:border-0",
              )}
              data-sidebar-item
              aria-current={isActive ? "page" : undefined}
              aria-label={item.name}
              onFocus={() => setFocusedElement("nav-item")}
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {!isCollapsed && (
                <span className="flex-1">
                  <TranslatedText text={item.name} />
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      <div className="p-4 border-t border-sidebar-border flex-shrink-0">
        {!isCollapsed ? (
          <div className="bg-card p-3 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                <span className="text-xs font-medium">
                  {(() => {
                    const fullName = (user as any)?.fullName || (user as any)?.name
                    if (fullName) {
                      const parts = fullName.trim().split(' ')
                      if (parts.length >= 2) {
                        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
                      }
                      return fullName.charAt(0).toUpperCase()
                    }
                    if (user?.firstName && user?.lastName) {
                      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
                    }
                    if (user?.firstName) {
                      return user.firstName.charAt(0).toUpperCase()
                    }
                    if (user?.email) {
                      return user.email.charAt(0).toUpperCase()
                    }
                    return 'U'
                  })()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {(user as any)?.fullName || (user as any)?.name || 
                   (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                   user?.firstName ||
                   (user?.email ? user.email.split('@')[0] : 'User')}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.role
                    ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
                    : 'Employee'}
                </p>
              </div>
              <button
                className="p-1 hover:bg-muted rounded focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1"
                aria-label="User settings"
                data-sidebar-item
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              className="w-8 h-8 bg-muted rounded-full flex items-center justify-center focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1"
              aria-label={`User profile: ${(user as any)?.fullName || (user as any)?.name || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'User')}`}
              data-sidebar-item
            >
              <span className="text-xs font-medium">
                {(() => {
                  const fullName = (user as any)?.fullName || (user as any)?.name
                  if (fullName) {
                    const parts = fullName.trim().split(' ')
                    if (parts.length >= 2) {
                      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
                    }
                    return fullName.charAt(0).toUpperCase()
                  }
                  if (user?.firstName && user?.lastName) {
                    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
                  }
                  if (user?.firstName) {
                    return user.firstName.charAt(0).toUpperCase()
                  }
                  if (user?.email) {
                    return user.email.charAt(0).toUpperCase()
                  }
                  return 'U'
                })()}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
